import { useState } from "react";
import { quickBookDurations, resolveQuickBooking } from "./time";

interface QuickBookButtonsProps {
  roomId: number;
  freeSlotStart: string;
  freeSlotDurationMinutes: number;
  onBook: (roomId: number, start: string, end: string) => void;
  durations?: number[];
  disabled?: boolean;
}

export function QuickBookButtons({
  roomId,
  freeSlotStart,
  freeSlotDurationMinutes,
  onBook,
  durations = [15, 30, 60],
  disabled = false,
}: QuickBookButtonsProps) {
  // The clock this row was last drawn against. A meeting-room card can sit on
  // screen for hours without re-rendering — a kiosk, a dashboard tab — so the
  // press re-reads the clock and books from *that*, never from whenever the
  // buttons happened to render (#344). Storing the fresh reading also redraws
  // the row, so a button the slot has since outgrown stops being offered
  // instead of quietly doing nothing.
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Durations are measured from where the booking would start, not from the
  // slot's nominal start — see ``quickBookDurations``. Same rule as
  // ``RoomDaySchedule``'s in-grid buttons: the two controls look identical, so
  // they must offer and book identically.
  const available = quickBookDurations(durations, freeSlotStart, freeSlotDurationMinutes, nowMs);
  if (available.length === 0) return null;

  const book = (minutes: number) => {
    const pressedAt = Date.now();
    setNowMs(pressedAt);
    const booking = resolveQuickBooking(
      freeSlotStart,
      freeSlotDurationMinutes,
      minutes,
      pressedAt,
    );
    if (booking) onBook(roomId, booking.start, booking.end);
  };

  return (
    <div style={{ display: "flex", gap: 4 }}>
      {available.map((d) => (
        <button
          key={d}
          onClick={() => book(d)}
          disabled={disabled}
          style={{
            fontSize: 11,
            padding: "3px 8px",
            borderRadius: 4,
            backgroundColor: disabled
              ? "var(--mantine-color-gray-filled)"
              : "var(--mantine-color-green-filled)",
            color: "var(--mantine-color-white)",
            border: "none",
            cursor: disabled ? "not-allowed" : "pointer",
            fontWeight: 500,
          }}
        >
          {d >= 60 ? `${d / 60}h` : `${d}m`}
        </button>
      ))}
    </div>
  );
}
